import { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { buildSearchIndex, extractSections, search } from '@/legal/utils/searchIndexer';
import type { SearchResult } from '@/legal/utils/searchIndexer';

interface Props {
  content: string;
}

export function DocumentSearch({ content }: Props) {
  const { t } = useTranslation('legal');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);

  const index = useMemo(() => buildSearchIndex(extractSections(content)), [content]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setResults(search(index, query));
    }, 200);
    return () => window.clearTimeout(timer);
  }, [query, index]);

  return (
    <div className="relative">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary" aria-hidden />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('search.placeholder')}
          className="h-9 w-full rounded-md border border-border bg-surface-raised pl-9 pr-3 text-sm text-text-primary placeholder:text-text-secondary focus:border-border-focus focus:outline-none"
        />
      </div>
      {query.length >= 2 && results.length > 0 && (
        <ul className="mt-2 max-h-80 overflow-y-auto rounded-md border border-border bg-surface-raised p-2 shadow-lg">
          {results.map((r) => (
            <li key={r.anchor}>
              <a
                href={`#${r.anchor}`}
                onClick={() => setQuery('')}
                className="block rounded p-2 text-sm hover:bg-surface-subtle"
              >
                <div className="font-medium text-text-primary">{r.title}</div>
                <div className="mt-1 line-clamp-2 text-xs text-text-secondary">{r.excerpt}</div>
              </a>
            </li>
          ))}
        </ul>
      )}
      {query.length >= 2 && results.length === 0 && (
        <div className="mt-2 rounded-md border border-border bg-surface-raised p-3 text-sm text-text-secondary">
          {t('search.noResults')}
        </div>
      )}
    </div>
  );
}
